import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faBox,
  faCheckCircle,
  faSpinner,
  faPhone,
  faUser,
  faMapMarkerAlt,
  faClock,
  faMotorcycle,
} from '@fortawesome/free-solid-svg-icons';
import { logisticsAPI } from '../services/api';
import DeliveryCodeDisplay from './DeliveryCodeDisplay';
import './DeliveryTracking.css';

const DeliveryTracking = ({ orderId, trackingId }) => {
  const [deliveryData, setDeliveryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (trackingId) {
      fetchDeliveryTracking();
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchDeliveryTracking, 30000);
      return () => clearInterval(interval);
    }
  }, [trackingId]);

  const fetchDeliveryTracking = async () => {
    try {
      setRefreshing(true);
      const response = await logisticsAPI.trackDelivery(trackingId);
      setDeliveryData(response.data || response);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load delivery tracking');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#f59e0b',
      'rider_assigned': '#3b82f6',
      'picked_up': '#8b5cf6',
      'in_transit': '#06b6d4',
      'out_for_delivery': '#10b981',
      'delivered': '#22c55e',
      'failed': '#ef4444',
    };
    return colors[status?.toLowerCase()] || '#9ca3af';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': faBox,
      'rider_assigned': faUser,
      'picked_up': faMotorcycle,
      'in_transit': faTruck,
      'out_for_delivery': faMapMarkerAlt,
      'delivered': faCheckCircle,
    };
    return icons[status?.toLowerCase()] || faBox;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="delivery-tracking-container loading">
        <FontAwesomeIcon icon={faSpinner} spin className="loading-spinner" />
        <p>Loading delivery tracking...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="delivery-tracking-container error">
        <p className="error-message">{error}</p>
        <button onClick={fetchDeliveryTracking} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  if (!deliveryData) {
    return (
      <div className="delivery-tracking-container empty">
        <p>No delivery tracking available yet</p>
      </div>
    );
  }

  const { status, rider, timeline, estimatedDelivery, currentLocation } = deliveryData;

  return (
    <div className="delivery-tracking-container">
      {/* Header with Status */}
      <div className="delivery-tracking-header">
        <div className="status-badge" style={{ backgroundColor: getStatusColor(status) }}>
          <FontAwesomeIcon icon={getStatusIcon(status)} />
          <span>{status?.replace(/_/g, ' ').toUpperCase()}</span>
        </div>
        <button
          onClick={fetchDeliveryTracking}
          className={`refresh-button ${refreshing ? 'spinning' : ''}`}
          disabled={refreshing}
        >
          <FontAwesomeIcon icon={faSpinner} spin={refreshing} />
          Refresh
        </button>
      </div>

      {/* Delivery Codes */}
      {orderId && status !== 'delivered' && (
        <DeliveryCodeDisplay orderId={orderId} type="delivery" />
      )}

      {/* Rider Information */}
      {rider && (
        <div className="rider-info-card">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faMotorcycle} />
            Rider Information
          </h3>
          <div className="rider-details">
            <div className="rider-avatar">
              {rider.photo ? (
                <img src={rider.photo} alt={rider.name} />
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
            </div>
            <div className="rider-info">
              <h4 className="rider-name">{rider.name || rider.riderName || 'Rider'}</h4>
              <p className="rider-id">ID: {rider.id || rider.riderId || 'N/A'}</p>
              {rider.rating && (
                <div className="rider-rating">
                  <span className="stars">{'⭐'.repeat(Math.floor(rider.rating))}</span>
                  <span className="rating-value">{rider.rating}</span>
                </div>
              )}
            </div>
            {rider.phone && (
              <a href={`tel:${rider.phone}`} className="call-rider-button">
                <FontAwesomeIcon icon={faPhone} />
                Call
              </a>
            )}
          </div>
        </div>
      )}

      {/* Estimated Delivery */}
      {estimatedDelivery && status !== 'delivered' && (
        <div className="estimated-delivery-card">
          <FontAwesomeIcon icon={faClock} className="clock-icon" />
          <div>
            <p className="label">Estimated Delivery</p>
            <p className="time">{formatTime(estimatedDelivery)}</p>
          </div>
        </div>
      )}

      {/* Current Location */}
      {currentLocation && status !== 'delivered' && (
        <div className="current-location-card">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="map-icon" />
          <div>
            <p className="label">Current Location</p>
            <p className="location">{currentLocation}</p>
          </div>
        </div>
      )}

      {/* Delivery Timeline */}
      {timeline && timeline.length > 0 && (
        <div className="delivery-timeline">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faTruck} />
            Delivery Timeline
          </h3>
          <div className="timeline-list">
            {timeline.map((event, index) => (
              <div
                key={index}
                className={`timeline-item ${event.completed ? 'completed' : 'pending'}`}
              >
                <div className="timeline-marker">
                  {event.completed ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                  ) : (
                    <div className="pending-dot"></div>
                  )}
                </div>
                <div className="timeline-content">
                  <h4 className="timeline-title">{event.title || event.status}</h4>
                  <p className="timeline-description">{event.description}</p>
                  {event.timestamp && (
                    <span className="timeline-time">{formatTime(event.timestamp)}</span>
                  )}
                  {event.location && (
                    <span className="timeline-location">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {event.location}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tracking ID */}
      <div className="tracking-id-footer">
        <span className="tracking-label">Tracking ID:</span>
        <code className="tracking-code">{trackingId}</code>
      </div>
    </div>
  );
};

export default DeliveryTracking;
